if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

      this.initTouchSupport();
      this.initThumbnailDragSupport();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    initTouchSupport() {
      const mainMedia = this.querySelector('[id^="Slider-Gallery"]');
      if (!mainMedia) return;

      let startX = 0;
      let currentX = 0;
      let isDragging = false;
      let startTime = 0;
      let initialTransform = 0;

      // Disable modal opening on mobile
      if (window.matchMedia('(max-width: 749px)').matches) {
        const modalOpeners = this.querySelectorAll('.product__modal-opener');
        modalOpeners.forEach(opener => {
          opener.style.pointerEvents = 'none';
          const button = opener.querySelector('.product__media-toggle');
          if (button) {
            button.disabled = true;
            button.style.display = 'none';
          }
        });
      }

      // Mouse events for desktop drag
      const handleMouseStart = (e) => {
        isDragging = true;
        startX = e.clientX;
        currentX = e.clientX;
        startTime = Date.now();
        initialTransform = 0;
        mainMedia.style.cursor = 'grabbing';
        mainMedia.style.userSelect = 'none';
        mainMedia.style.transition = 'none';
        e.preventDefault();
      };

      const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        currentX = e.clientX;
        
        // Calculate drag distance and apply transform
        const deltaX = currentX - startX;
        const dragPercent = Math.min(Math.max(deltaX / mainMedia.offsetWidth, -0.5), 0.5);
        const translateX = dragPercent * 100;
        
        mainMedia.style.transform = `translateX(${translateX}%)`;
        
        // Add visual feedback for direction
        if (Math.abs(deltaX) > 20) {
          mainMedia.style.opacity = Math.max(0.7, 1 - Math.abs(dragPercent) * 0.6);
        }
      };

      const handleMouseEnd = (e) => {
        if (!isDragging) return;
        isDragging = false;
        mainMedia.style.cursor = 'grab';
        mainMedia.style.userSelect = '';
        
        const deltaX = currentX - startX;
        const deltaTime = Date.now() - startTime;
        const threshold = 50;
        
        // Reset transform with animation
        mainMedia.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
        mainMedia.style.transform = 'translateX(0)';
        mainMedia.style.opacity = '1';
        
        // Change media if threshold met
        if (Math.abs(deltaX) > threshold || (Math.abs(deltaX) > 20 && deltaTime < 300)) {
          setTimeout(() => {
            if (deltaX > 0) {
              this.previousMedia();
            } else {
              this.nextMedia();
            }
            // Reset transition after media change
            setTimeout(() => {
              mainMedia.style.transition = '';
            }, 300);
          }, 150);
        } else {
          // Reset transition if no media change
          setTimeout(() => {
            mainMedia.style.transition = '';
          }, 300);
        }
      };

      // Touch events for mobile
      const handleTouchStart = (e) => {
        startX = e.touches[0].clientX;
        currentX = e.touches[0].clientX;
        startTime = Date.now();
        initialTransform = 0;
        mainMedia.style.transition = 'none';
      };

      const handleTouchMove = (e) => {
        currentX = e.touches[0].clientX;
        
        // Calculate drag distance and apply transform
        const deltaX = currentX - startX;
        const dragPercent = Math.min(Math.max(deltaX / mainMedia.offsetWidth, -0.5), 0.5);
        const translateX = dragPercent * 100;
        
        mainMedia.style.transform = `translateX(${translateX}%)`;
        
        // Add visual feedback for direction
        if (Math.abs(deltaX) > 20) {
          mainMedia.style.opacity = Math.max(0.7, 1 - Math.abs(dragPercent) * 0.6);
        }
      };

      const handleTouchEnd = (e) => {
        const deltaX = currentX - startX;
        const deltaTime = Date.now() - startTime;
        const threshold = 50;
        
        // Reset transform with animation
        mainMedia.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
        mainMedia.style.transform = 'translateX(0)';
        mainMedia.style.opacity = '1';
        
        // Change media if threshold met
        if (Math.abs(deltaX) > threshold || (Math.abs(deltaX) > 20 && deltaTime < 300)) {
          setTimeout(() => {
            if (deltaX > 0) {
              this.previousMedia();
            } else {
              this.nextMedia();
            }
            // Reset transition after media change
            setTimeout(() => {
              mainMedia.style.transition = '';
            }, 300);
          }, 150);
        } else {
          // Reset transition if no media change
          setTimeout(() => {
            mainMedia.style.transition = '';
          }, 300);
        }
      };

      // Add mouse event listeners
      mainMedia.addEventListener('mousedown', handleMouseStart);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseEnd);
      
      // Prevent context menu on right click while dragging
      mainMedia.addEventListener('contextmenu', (e) => {
        if (isDragging) e.preventDefault();
      });

      // Add touch event listeners
      mainMedia.addEventListener('touchstart', handleTouchStart, { passive: true });
      mainMedia.addEventListener('touchmove', handleTouchMove, { passive: true });
      mainMedia.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    initThumbnailDragSupport() {
      const thumbnailSlider = this.querySelector('.thumbnail-list.slider');
      if (!thumbnailSlider) return;

      let startX = 0;
      let currentX = 0;
      let isDragging = false;
      let startScrollLeft = 0;

      // Mouse events for desktop drag
      const handleMouseStart = (e) => {
        isDragging = true;
        startX = e.clientX;
        currentX = e.clientX;
        startScrollLeft = thumbnailSlider.scrollLeft;
        thumbnailSlider.style.cursor = 'grabbing';
        thumbnailSlider.style.userSelect = 'none';
        thumbnailSlider.style.scrollBehavior = 'auto';
        e.preventDefault();
      };

      const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        currentX = e.clientX;
        
        const deltaX = currentX - startX;
        thumbnailSlider.scrollLeft = startScrollLeft - deltaX;
      };

      const handleMouseEnd = (e) => {
        if (!isDragging) return;
        isDragging = false;
        thumbnailSlider.style.cursor = 'grab';
        thumbnailSlider.style.userSelect = '';
        thumbnailSlider.style.scrollBehavior = 'smooth';
      };

      // Touch events for mobile
      const handleTouchStart = (e) => {
        startX = e.touches[0].clientX;
        currentX = e.touches[0].clientX;
        startScrollLeft = thumbnailSlider.scrollLeft;
        thumbnailSlider.style.scrollBehavior = 'auto';
      };

      const handleTouchMove = (e) => {
        currentX = e.touches[0].clientX;
        const deltaX = currentX - startX;
        thumbnailSlider.scrollLeft = startScrollLeft - deltaX;
      };

      const handleTouchEnd = (e) => {
        thumbnailSlider.style.scrollBehavior = 'smooth';
      };

      // Add mouse event listeners
      thumbnailSlider.addEventListener('mousedown', handleMouseStart);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseEnd);
      
      // Prevent context menu on right click while dragging
      thumbnailSlider.addEventListener('contextmenu', (e) => {
        if (isDragging) e.preventDefault();
      });

      // Add touch event listeners
      thumbnailSlider.addEventListener('touchstart', handleTouchStart, { passive: true });
      thumbnailSlider.addEventListener('touchmove', handleTouchMove, { passive: true });
      thumbnailSlider.addEventListener('touchend', handleTouchEnd, { passive: true });
      
      // Set initial cursor
      thumbnailSlider.style.cursor = 'grab';
    }

    nextMedia() {
      const activeMedia = this.querySelector('.is-active');
      const nextMedia = activeMedia.nextElementSibling || this.querySelector('.product__media-item:first-child');
      if (nextMedia && nextMedia !== activeMedia) {
        this.setActiveMedia(nextMedia.dataset.mediaId);
      }
    }

    previousMedia() {
      const activeMedia = this.querySelector('.is-active');
      const prevMedia = activeMedia.previousElementSibling || this.querySelector('.product__media-item:last-child');
      if (prevMedia && prevMedia !== activeMedia) {
        this.setActiveMedia(prevMedia.dataset.mediaId);
      }
    }
  });
}
